import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import FaqSection from './components/FaqSection';

// --- ✨ FINAL & COMPLETE: Data for all 18 tour packages ---
const tourPackages = [
    {
        title: "Birling Gap, Seven Sisters & Brighton",
        description: "Experience the stunning white cliffs of Seven Sisters, the beautiful pebble beach of Brighton, and enjoy water sports.",
        imageUrl: "/images/tours/brighton.jpg",
    },
    {
        title: "Lake District & Blackpool",
        description: "A scenic tour of the Lake District, including a Windermere lake cruise, Kirkstone Pass, and a visit to the lively Blackpool seafront.",
        imageUrl: "/images/tours/windermere.jpg",
    },
    {
        title: "Scotland (3-Day Trip)",
        description: "An epic 3-day journey to the Scottish Highlands, with stops on the way at the famous Balaji Temple in Birmingham, the stunning Lake District, and Gretna Green on the Scottish border.",
        imageUrl: "/images/tours/Scotland.jpg",
    },
    {
        title: "Cornwall & St Michael's Mount",
        description: "A coastal adventure to see Tintagel Castle, the iconic St Michael's Mount, the Minack Theatre, and the famous Land's End.",
        imageUrl: "/images/tours/St_Michaels.jpg",
    },
    {
        title: "Windsor Castle & Bicester Village",
        description: "Visit the historic Windsor Castle and then enjoy luxury shopping at the famous Bicester Village designer outlet.",
        imageUrl: "/images/tours/windsor_castle.jpg",
    },
    {
        title: "Peppa Pig World & Paultons Park",
        description: "A perfect family day out. Enjoy the rides and attractions at Peppa Pig World and the Lost Kingdom at Paultons Park.",
        imageUrl: "/images/tours/The_Edge.jpg",
    },
    {
        title: "Mahakali & Skanda Vale Temple Tour",
        description: "A spiritual journey to visit the Skanda Vale, Shree Maha Kali Temple, and the Lord Ranganatha Temple.",
        imageUrl: "/images/tours/Shree_Mahakali.jpg",
    },
    {
        title: "West Midland Safari Park",
        description: "An exciting safari adventure with animal encounters, a Penguin Feed, Sea Lion Show, and thrilling rides.",
        imageUrl: "/images/tours/Midland.jpeg",
    },
    {
        title: "Snowdonia Mountain Railway",
        description: "Witness heaven on earth. See Llandudno Beach, ride Britain's longest cable car, and ascend Snowdon Mountain.",
        imageUrl: "/images/tours/SnowdoniaMountain.jpg",
    },
    {
        title: "Isle of Wight & The Needles",
        description: "An island getaway featuring the famous Needles park, a chairlift with stunning views, and relaxing at Sandown beach.",
        imageUrl: "/images/tours/Needles_Park.jpg",
    },
    {
        title: "Bath & Stonehenge",
        description: "A tour through history visiting the mysterious Stonehenge and exploring the ancient Roman Baths in Bath city.",
        imageUrl: "/images/tours/bath_christmas.jpg",
    },
    {
        title: "Longleat Safari Park",
        description: "A drive-through safari adventure! See magnificent animals and explore Longleat House and the Jungle Cruise.",
        imageUrl: "/images/tours/longleat_lions.jpg",
    },
    {
        title: "Cadbury World & Balaji Temple",
        description: "A fun-filled day out visiting the Shri Venkateswara Balaji Temple and enjoying the chocolatey world of Cadbury.",
        imageUrl: "/images/tours/balaji_temple.jpg",
    },
    {
        title: "Cotswolds Village Tour",
        description: "Visit the most picturesque villages in England, including Bibury's Arlington Row and the charming Bourton-on-the-water.",
        imageUrl: "/images/tours/cotsworld.jpeg",
    },
    {
        title: "Durdle Door & Lulworth Cove",
        description: "See the magnificent rock arch of Durdle Door and visit the picturesque, shell-shaped Lulworth Cove on the Jurassic Coast.",
        imageUrl: "/images/tours/lulworth-cove.jpg",
    },
    {
        title: "Paris (4 Days/3 Nights)",
        description: "An amazing European getaway to Paris, including the Eiffel Tower, Disneyland, and the stunning Versailles Palace.",
        imageUrl: "/images/tours/paris1.jpg",
    },
    {
        title: "Leeds Castle, Canterbury & Dover",
        description: "Explore the 'loveliest castle in the world' at Leeds, visit the historic Canterbury Cathedral, and see the White Cliffs of Dover.",
        imageUrl: "/images/tours/Canterbury.avif",
    },
     {
        title: "Oxford, Stratford & Cotswolds",
        description: "A classic tour of England, visiting the historic university city of Oxford, Shakespeare's birthplace, and a beautiful Cotswolds village.",
        imageUrl: "/images/tours/Oxford.avif",
    }
];


const boardingPoints = ["Wembley", "East Ham", "Ilford", "Other (mention in notes)"];

const inputClass = "w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500";
const labelClass = "block text-sm font-semibold text-green-900 dark:text-green-100 mb-1";

const initialForm = { name: "", tour: "", date: "", adults: 1, kids: 0, babies: 0, boarding: "", notes: "" };

function UkTourTrips() {
    const contactWhatsAppNumber = "919703491968";
    const [form, setForm] = useState(initialForm);
    const formRef = useRef(null);
    const [today, setToday] = useState("");

    // Set after mount so the react-snap prerender doesn't bake in the build date
    useEffect(() => setToday(new Date().toISOString().split("T")[0]), []);

    const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });
    const needsNotes = form.tour.startsWith("Other") || form.boarding.startsWith("Other");

    const enquireAbout = (tourTitle) => {
        setForm({ ...form, tour: tourTitle });
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const formattedDate = new Date(`${form.date}T00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
        const lines = [
            "Hi! I'd like to book a UK tour.",
            "",
            `*Name:* ${form.name}`,
            `*Tour:* ${form.tour}`,
            `*Date:* ${formattedDate}`,
            `*Adults:* ${form.adults}`,
            `*Kids (3-11):* ${form.kids}`,
            `*Babies (0-2):* ${form.babies}`,
            `*Boarding point:* ${form.boarding}`,
        ];
        if (form.notes.trim()) lines.push(`*Notes:* ${form.notes.trim()}`);
        lines.push("", "Please share availability and price. Thanks!");
        const url = `https://wa.me/${contactWhatsAppNumber}?text=${encodeURIComponent(lines.join("\n"))}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    return (
        <>
            <Helmet>
                <title>UK Day Trips & Scotland Tours for Indians from London</title>
                <meta 
                    name="description" 
                    content="Affordable coach trips and day tours from London for Indians: Scotland, Cotswolds, Lake District, Bath & Stonehenge and more. Enquire on WhatsApp for dates."
                />
                {/* Social previews (WhatsApp, Facebook, X) — otherwise the homepage values are used */}
                <meta property="og:title" content="UK Day Trips & Scotland Tours for Indians from London" />
                <meta property="og:description" content="Affordable coach trips and day tours from London for Indians: Scotland, Cotswolds, Lake District, Bath & Stonehenge and more. Enquire on WhatsApp for dates." />
                <meta name="twitter:title" content="UK Day Trips & Scotland Tours for Indians from London" />
                <meta name="twitter:description" content="Affordable coach trips and day tours from London for Indians: Scotland, Cotswolds, Lake District, Bath & Stonehenge and more. Enquire on WhatsApp for dates." />
            </Helmet>

            <div className="container mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md my-6">
                <div className="max-w-5xl mx-auto text-left">
                    <h1 className="text-3xl md:text-4xl font-extrabold text-indigo-700 dark:text-indigo-300 mb-4 text-center">
                        UK Coach Trips for the Indian Community
                    </h1>
                    <p className="text-lg text-gray-700 dark:text-gray-300 mb-8 text-center">
                        Explore the best of the UK with our curated day trips and weekend tours. Departing from popular Indian areas in London like Wembley, East Ham, and Ilford, our partners make exploring easy and affordable.
                    </p>

                    <div ref={formRef} className="bg-green-50 dark:bg-green-900 border-2 border-dashed border-green-500 rounded-lg p-6 mb-12 scroll-mt-6">
                        <h2 className="text-2xl font-bold text-green-800 dark:text-green-200 mb-2 text-center">Get Tour Dates & Prices</h2>
                        <p className="text-green-700 dark:text-green-300 mb-6 text-center">Fill in your trip details and we'll open WhatsApp with your enquiry ready to send.</p>
                        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className={labelClass} htmlFor="tt-name">Your name</label>
                                <input id="tt-name" className={inputClass} value={form.name} onChange={update("name")} required />
                            </div>
                            <div>
                                <label className={labelClass} htmlFor="tt-tour">Tour</label>
                                <select id="tt-tour" className={inputClass} value={form.tour} onChange={update("tour")} required>
                                    <option value="" disabled>Select a tour</option>
                                    {tourPackages.map((tour) => (
                                        <option key={tour.title} value={tour.title}>{tour.title}</option>
                                    ))}
                                    <option value="Other (mention in notes)">Other (mention in notes)</option>
                                </select>
                            </div>
                            <div>
                                <label className={labelClass} htmlFor="tt-date">Travel date</label>
                                <input id="tt-date" type="date" min={today} className={inputClass} value={form.date} onChange={update("date")} required />
                            </div>
                            <div>
                                <label className={labelClass} htmlFor="tt-boarding">Boarding point</label>
                                <select id="tt-boarding" className={inputClass} value={form.boarding} onChange={update("boarding")} required>
                                    <option value="" disabled>Select boarding point</option>
                                    {boardingPoints.map((point) => (
                                        <option key={point} value={point}>{point}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="md:col-span-2 grid grid-cols-3 gap-4">
                                <div>
                                    <label className={labelClass} htmlFor="tt-adults">Adults</label>
                                    <input id="tt-adults" type="number" min="1" max="50" className={inputClass} value={form.adults} onChange={update("adults")} required />
                                </div>
                                <div>
                                    <label className={labelClass} htmlFor="tt-kids">Kids (3-11)</label>
                                    <input id="tt-kids" type="number" min="0" max="50" className={inputClass} value={form.kids} onChange={update("kids")} required />
                                </div>
                                <div>
                                    <label className={labelClass} htmlFor="tt-babies">Babies (0-2)</label>
                                    <input id="tt-babies" type="number" min="0" max="20" className={inputClass} value={form.babies} onChange={update("babies")} required />
                                </div>
                            </div>
                            <div className="md:col-span-2">
                                <label className={labelClass} htmlFor="tt-notes">Notes {needsNotes ? "(please tell us which tour / boarding point)" : "(optional)"}</label>
                                <textarea id="tt-notes" rows="2" className={inputClass} placeholder="Other tour or boarding location, food preference, wheelchair access..." value={form.notes} onChange={update("notes")} required={needsNotes} />
                            </div>
                            <div className="md:col-span-2 text-center">
                                <button
                                    type="submit"
                                    className="inline-flex items-center justify-center px-8 py-3 rounded-full bg-green-500 hover:bg-green-600 text-white font-bold text-lg transition duration-200 shadow-md transform hover:scale-105"
                                >
                                   <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 24 24"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.894 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.886-.001 2.267.651 4.383 1.803 6.166l-1.225 4.469 4.575-1.201zM18.33 14.229c-.301-.15-1.767-.867-2.04-.967-.272-.1-.47-.15-.67.15-.197.299-.771.966-.944 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.299-.018-.462.13-.611.134-.133.297-.347.446-.52.15-.174.199-.299.298-.498.1-.199.05-.374-.025-.524-.075-.15-.67-1.612-.916-2.206-.242-.58-.487-.5-.67-.506-.183-.006-.38-.006-.579-.006-.199 0-.523.074-.797.373-.273.3-.981.957-.981 2.322 0 1.365 1.007 2.699 1.157 2.897.149.199 1.958 3.024 4.793 4.22 2.836 1.196 2.836.797 3.344.746.508-.05 1.767-.717 2.016-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.199-.57-.347z"/></svg>
                                    Send Enquiry on WhatsApp
                                </button>
                            </div>
                        </form>
                    </div>

                    <div>
                        <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-6 text-center">Popular Tour Packages</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {tourPackages.map((tour, index) => (
                                <div key={index} className="bg-gray-50 dark:bg-gray-900 rounded-lg shadow-lg overflow-hidden flex flex-col transform hover:-translate-y-2 transition-transform duration-300">
                                    <img src={tour.imageUrl} alt={tour.title} loading="lazy" decoding="async" className="w-full h-48 object-cover" />
                                    <div className="p-4 flex flex-col flex-grow">
                                        <h3 className="font-bold text-lg text-indigo-700 dark:text-indigo-400 mb-2">{tour.title}</h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 flex-grow">{tour.description}</p>
                                        <button
                                            type="button"
                                            onClick={() => enquireAbout(tour.title)}
                                            className="mt-4 self-start px-4 py-2 rounded-full bg-green-500 hover:bg-green-600 text-white text-sm font-semibold transition duration-200"
                                        >
                                            Enquire about this tour
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <FaqSection faqs={[
                        { q: "What are the best weekend trips from London for Indians?", a: "Popular weekend trips for the Indian community include 3-day coach tours to the Scottish Highlands, day trips to see the white cliffs at Seven Sisters & Brighton, and historical tours to Bath & Stonehenge. We partner with operators who often provide Indian meal options." },
                        { q: "How much does a coach tour to Scotland from London cost?", a: "A typical 3-day weekend coach tour to Scotland from London, including travel and accommodation, can range from £150 to £250 per person, depending on the season and the operator. Contact us on WhatsApp for the latest deals and group discounts." },
                    ]} />
                </div>
            </div>
        </>
    );
}

export default UkTourTrips;